import { array, nonEmptyArray, number, ord, separated, string } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { Separated } from 'fp-ts/Separated';
import { Restriction, RestrictionConditions } from '@mews-commander/data-access';
import { Dependencies, RowConditions } from '../types';

const arrayFromSeparatedRL = <T>({ left, right }: Separated<Array<T>, Array<T>>) => [...right, ...left];

const restrictionTypeOrderingMap = {
    ClosedToStay: 0,
    ClosedToArrival: 1,
    ClosedToDeparture: 2,
};

const sortRestrictionType = array.sort(
    ord.contramap(({ type }: RestrictionConditions) => restrictionTypeOrderingMap[type])(number.Ord)
);

const sortResourceCategoryType = array.sort(
    ord.contramap(({ resourceCategoryType }: RestrictionConditions) => resourceCategoryType ?? string.empty)(string.Ord)
);

const sortResourceCategories = ({ resourceCategories }: Dependencies) =>
    array.sort(
        ord.contramap(({ resourceCategoryId }: RestrictionConditions) =>
            resourceCategoryId ? resourceCategories[resourceCategoryId].ordering : 0
        )(number.Ord)
    );

const sortResourceCategoryConditions = (dependencies: Dependencies) =>
    flow(
        array.partition(
            ({ resourceCategoryType, resourceCategoryId }: RestrictionConditions) =>
                resourceCategoryType === null && resourceCategoryId === null
        ),
        separated.mapLeft(
            flow(
                array.partition(({ resourceCategoryType }: RestrictionConditions) => resourceCategoryType === null),
                separated.bimap(sortResourceCategoryType, sortResourceCategories(dependencies)),
                ({ left: resourceCategoryTypeConditions, right: resourceCategoryConditions }) => {
                    const resourceCategoryTypeConditionsByType = pipe(
                        resourceCategoryTypeConditions,
                        nonEmptyArray.groupBy(({ type }) => type)
                    );

                    const {
                        left: withoutResourceCategoryTypeInConditions,
                        right: withResourceCategoryTypeInConditions,
                    } = pipe(
                        resourceCategoryConditions,
                        array.partition(({ resourceCategoryId }) => {
                            const resourceCategoryType = resourceCategoryId
                                ? dependencies.resourceCategories[resourceCategoryId].type
                                : null;
                            return resourceCategoryType
                                ? resourceCategoryTypeConditionsByType[resourceCategoryType] !== undefined
                                : false;
                        })
                    );

                    const conditionsGroupedByResourceCategoryType = pipe(
                        withResourceCategoryTypeInConditions,
                        nonEmptyArray.groupBy(({ resourceCategoryId }) =>
                            resourceCategoryId ? dependencies.resourceCategories[resourceCategoryId].type : string.empty
                        )
                    );

                    return pipe(
                        resourceCategoryTypeConditions,
                        array.flatMap((condition, index) => {
                            const nextCondition = resourceCategoryTypeConditions[index + 1];
                            return condition.resourceCategoryType &&
                                (!nextCondition ||
                                    nextCondition.resourceCategoryType !== condition.resourceCategoryType) &&
                                conditionsGroupedByResourceCategoryType[condition.resourceCategoryType]
                                ? [
                                      condition,
                                      ...conditionsGroupedByResourceCategoryType[condition.resourceCategoryType],
                                  ]
                                : [condition];
                        }),
                        array.concat(withoutResourceCategoryTypeInConditions)
                    );
                }
            )
        ),
        arrayFromSeparatedRL
    );

const sortRateGroup = ({ rateGroups }: Dependencies) =>
    flow(
        nonEmptyArray.groupBy(({ rateGroupId }: RestrictionConditions) => rateGroupId ?? string.empty),
        Object.values<Array<RestrictionConditions>>,
        array.flatten,
        array.sort(
            ord.contramap(({ rateGroupId }: RestrictionConditions) =>
                rateGroupId ? rateGroups[rateGroupId].ordering : 0
            )(number.Ord)
        )
    );

const sortRate = ({ rates }: Dependencies) =>
    flow(
        nonEmptyArray.groupBy(
            ({ baseRateId, exactRateId }: RestrictionConditions) => baseRateId ?? exactRateId ?? string.empty
        ),
        Object.values<Array<RestrictionConditions>>,
        array.flatten,
        array.sort(
            ord.contramap(({ baseRateId, exactRateId }: RestrictionConditions) => {
                const rateId = baseRateId ?? exactRateId;
                return rateId ? rates[rateId].ordering : 0;
            })(number.Ord)
        )
    );

const sortRateConditions = (dependencies: Dependencies) =>
    flow(
        sortResourceCategoryConditions(dependencies),
        array.partition(({ rateGroupId }: RestrictionConditions) => rateGroupId === null),
        separated.bimap(sortRateGroup(dependencies), sortRate(dependencies)),
        ({ left: rateGroupConditions, right: rateConditions }) => {
            const rateGroupConditionsByRateGroup = pipe(
                rateGroupConditions,
                nonEmptyArray.groupBy(({ rateGroupId }) => rateGroupId ?? string.empty)
            );

            const { left: withoutRateGroupInConditions, right: withRateGroupInConditions } = pipe(
                rateConditions,
                array.partition(({ baseRateId, exactRateId }) => {
                    const rateId = baseRateId ?? exactRateId;
                    const rateGroupId = rateId ? dependencies.rates[rateId].rateGroupId : null;
                    return rateGroupId ? rateGroupConditionsByRateGroup[rateGroupId] !== undefined : false;
                })
            );

            const conditionsGroupedByRateGroupId = pipe(
                withRateGroupInConditions,
                nonEmptyArray.groupBy(({ baseRateId, exactRateId }) => {
                    const rateId = baseRateId ?? exactRateId;
                    return rateId ? dependencies.rates[rateId].rateGroupId : string.empty;
                })
            );

            return pipe(
                rateGroupConditions,
                array.flatMap((condition, index) => {
                    const nextCondition = rateGroupConditions[index + 1];
                    return condition.rateGroupId &&
                        (!nextCondition || nextCondition.rateGroupId !== condition.rateGroupId) &&
                        conditionsGroupedByRateGroupId[condition.rateGroupId]
                        ? [condition, ...conditionsGroupedByRateGroupId[condition.rateGroupId]]
                        : [condition];
                }),
                array.concat(withoutRateGroupInConditions)
            );
        }
    );

const createSortedRowConditions = (
    restrictions: Array<Restriction>,
    dependencies: Dependencies
): Array<RowConditions> =>
    pipe(
        restrictions,
        array.map(({ conditions }) => conditions),
        sortRestrictionType,
        array.partition(
            ({ rateGroupId, baseRateId, exactRateId, resourceCategoryType, resourceCategoryId }) =>
                rateGroupId === null &&
                baseRateId === null &&
                exactRateId === null &&
                resourceCategoryType === null &&
                resourceCategoryId === null
        ),
        separated.mapLeft(
            flow(
                array.partition(
                    ({ rateGroupId, baseRateId, exactRateId }) =>
                        rateGroupId === null && baseRateId === null && exactRateId === null
                ),
                separated.bimap(sortRateConditions(dependencies), sortResourceCategoryConditions(dependencies)),
                arrayFromSeparatedRL
            )
        ),
        arrayFromSeparatedRL,
        array.map(({ type, rateGroupId, baseRateId, exactRateId, resourceCategoryType, resourceCategoryId }) => ({
            rateGroupId,
            rateId: baseRateId ?? exactRateId,
            resourceCategoryId,
            resourceCategoryType,
            type,
        })),
        array.uniq({
            equals: (a, b) =>
                a.type === b.type &&
                a.rateGroupId === b.rateGroupId &&
                a.rateId === b.rateId &&
                a.resourceCategoryType === b.resourceCategoryType &&
                a.resourceCategoryId === b.resourceCategoryId,
        })
    );

export default createSortedRowConditions;
